import { FunctionComponent } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { classNames } from "@/utils/classNames";

import { ContentStatus, SegmentStatusFilter } from "../schema/knowledgeDb";
import { Product, ProductType, PRODUCT_TYPE_LABELS } from "../schema/productsServices";

interface ProductSidebarProps {
	products: Product[];
	activeProductId: number | null;
	statusFilter: SegmentStatusFilter;
	selectedProductIds: Set<number>;
	onProductSelect: (productId: number) => void;
	onAddProduct: () => void;
	onStatusFilterChange: (filter: SegmentStatusFilter) => void;
	onToggleProductSelection: (productId: number) => void;
	onSelectAll: (productIds: number[]) => void;
	onBulkApprove: () => void;
}

const TYPE_BADGE_COLORS: Record<ProductType, string> = {
	[ProductType.PRODUCT]: "bg-blue-100 text-blue-700",
	[ProductType.SERVICE]: "bg-green-100 text-green-700",
	[ProductType.FRAMEWORK]: "bg-purple-100 text-purple-700",
};

const isProductVisible = (product: Product, filter: SegmentStatusFilter) => {
	if (filter === SegmentStatusFilter.ALL) {
		return true;
	}

	if (filter === SegmentStatusFilter.AI_DRAFTED) {
		return product.status === ContentStatus.AI_DRAFTED;
	}

	return product.status === ContentStatus.HUMAN_APPROVED;
};

export const ProductSidebar: FunctionComponent<ProductSidebarProps> = ({
	products,
	activeProductId,
	statusFilter,
	selectedProductIds,
	onProductSelect,
	onAddProduct,
	onStatusFilterChange,
	onToggleProductSelection,
	onSelectAll,
	onBulkApprove,
}) => {
	const visibleProducts = products.filter((p) => isProductVisible(p, statusFilter));
	const isBulkMode = selectedProductIds.size > 0;
	const visibleProductIds = visibleProducts.map((p) => p.id);
	const allSelected =
		visibleProductIds.length > 0 && visibleProductIds.every((id) => selectedProductIds.has(id));
	const someSelected = visibleProductIds.some((id) => selectedProductIds.has(id));

	return (
		<div className="w-[280px] min-w-[280px] border-r border-gray-200 bg-brand-white flex flex-col sticky top-0 h-full overflow-y-auto">
			{/* Header */}
			<div className="px-4 py-3 border-b border-gray-200">
				<h3 className="text-sm font-semibold text-gray-900">Products & Services</h3>
				<p className="text-xs text-gray-500 mt-1">{visibleProducts.length} items</p>
			</div>

			{/* Status Filter */}
			<div className="px-4 py-2 border-b border-gray-200">
				<select
					className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
					value={statusFilter}
					onChange={(e) => onStatusFilterChange(e.target.value as SegmentStatusFilter)}
				>
					<option value={SegmentStatusFilter.ALL}>All Products</option>
					<option value={SegmentStatusFilter.AI_DRAFTED}>AI-drafted only</option>
					<option value={SegmentStatusFilter.APPROVED}>Approved only</option>
				</select>
			</div>

			{/* Select all */}
			{visibleProductIds.length > 0 && (
				<div className="px-4 py-2 border-b border-gray-200">
					<Checkbox
						isChecked={allSelected}
						isIndeterminate={someSelected && !allSelected}
						onChange={() => onSelectAll(allSelected ? [] : visibleProductIds)}
					>
						<span className="text-xs font-medium text-gray-600">
							Select all ({visibleProductIds.length})
						</span>
					</Checkbox>
				</div>
			)}

			{/* Product list */}
			<div className="flex-1 overflow-y-auto py-2">
				{visibleProducts.map((product) => (
					<div
						key={product.id}
						className={classNames(
							"flex items-center w-full px-4 py-2.5 text-left text-sm transition-colors",
							activeProductId === product.id
								? "bg-brand-light/10 text-brand-light font-medium border-l-2 border-brand-light"
								: "text-gray-700 hover:bg-gray-200/50 border-l-2 border-transparent"
						)}
					>
						{/* Bulk selection checkbox */}
						<div className="mr-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
							<Checkbox
								isChecked={selectedProductIds.has(product.id)}
								onChange={() => onToggleProductSelection(product.id)}
							/>
						</div>
						<button
							className="flex-1 flex items-center justify-between text-left cursor-pointer truncate"
							type="button"
							onClick={() => onProductSelect(product.id)}
						>
							<span className="truncate mr-2">{product.name}</span>
							<div className="flex items-center gap-1 flex-shrink-0">
								<span
									className={classNames(
										"inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap",
										TYPE_BADGE_COLORS[product.type]
									)}
								>
									{PRODUCT_TYPE_LABELS[product.type]}
								</span>
								{product.status === ContentStatus.AI_DRAFTED && (
									<span className="text-[10px]">✨</span>
								)}
								{product.status === ContentStatus.HUMAN_APPROVED && (
									<span className="text-[10px] text-status-success">✓</span>
								)}
							</div>
						</button>
					</div>
				))}
			</div>

			{/* Bulk approve action bar */}
			{isBulkMode && (
				<div className="sticky bottom-0 px-4 py-3 border-t border-gray-200 bg-white">
					<button
						className="w-full px-3 py-2 text-xs font-medium text-white bg-status-warning rounded-lg hover:bg-status-warning/90 transition-colors"
						type="button"
						onClick={onBulkApprove}
					>
						✓ Approve Selected ({selectedProductIds.size})
					</button>
				</div>
			)}

			{/* Add button */}
			<div className="px-4 py-3 border-t border-gray-200">
				<button
					className="flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-brand-light bg-brand-light/10 rounded-lg hover:bg-brand-light/20 transition-colors cursor-pointer"
					type="button"
					onClick={onAddProduct}
				>
					+ Add Product/Service
				</button>
			</div>
		</div>
	);
};
